import React from 'react'
import { useHistory } from 'react-router-dom'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Stack,
  Table,
  Text,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast
} from '@chakra-ui/react'
import * as Sentry from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { formatMoney } from '@homevest/utils/numbers'

import axios from 'lib/axios'
import { RentalApplicationInitialUnderwritingDataQuery } from 'graphql/generated'

export const PreApprovalModal = ({
  initialUnderwritingData,
  isOpen,
  onClose
}: {
  initialUnderwritingData:
    | RentalApplicationInitialUnderwritingDataQuery['rental_applications_by_pk']
    | undefined
  isOpen: boolean
  onClose: () => void
}) => {
  const toast = useToast()
  const history = useHistory()

  const preApprovedDataMissing = initialUnderwritingData?.lead_group?.lead_group_users.some(
    (lgu) => {
      return (
        lgu.user.user_underwriting_data[0]?.credit_score === null ||
        lgu.user.user_underwriting_data[0]?.verified_income === null
      )
    }
  )

  const { isLoading, mutate: preApprove } = useMutation({
    mutationFn: async () => {
      await axios.post(`/admin/saferent/${initialUnderwritingData!.id}/preapprove`)
    },
    onSuccess: () => {
      toast({
        title: 'Application Pre-Approved',
        description: 'The application was successfully pre-approved!',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      })
      onClose()
      history.go(0) // refresh the page so that app status is updated
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: `Cannot pre-approve application: ${error.message ?? ''}.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom-right'
      })
      Sentry.captureException(error)
    }
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxW='800px'>
        <ModalHeader>Pre-Approve Rental Application</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {initialUnderwritingData ? (
            <>
              {preApprovedDataMissing && (
                <Text color='red.500' fontSize='sm'>
                  Please input the missing credit score or gross annual income in the{' '}
                  <strong>Saferent Panel</strong> before pre-approving.
                </Text>
              )}
              <Stack spacing={4}>
                <Table variant='simple'>
                  <Thead>
                    <Tr>
                      <Th>Application Name</Th>
                      <Th>Gross Annual Income</Th>
                      <Th>Credit Score</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {initialUnderwritingData.lead_group?.lead_group_users?.map((lgu) => (
                      <Tr key={lgu.user.id}>
                        <Td>
                          {lgu.user.first_name} {lgu.user.last_name}
                        </Td>
                        <Td>
                          {lgu.user.user_underwriting_data[0]?.verified_income
                            ? formatMoney(
                                lgu.user.user_underwriting_data[0]?.verified_income,
                                2,
                                '$'
                              )
                            : '-'}
                        </Td>
                        <Td>{lgu.user.user_underwriting_data[0]?.credit_score ?? '-'}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Stack>
            </>
          ) : (
            <Text>
              Unable to load initial underwriting data. Please try again. If the problem persists,
              please contact Engineering.
            </Text>
          )}
        </ModalBody>
        <ModalFooter>
          <Stack w='100%'>
            <Text alignSelf='flex-start' fontSize='xs'>
              Clicking "Pre-Approve" will trigger a SafeRent criminal background check for all
              applicants.
            </Text>
            <Button
              alignSelf='flex-end'
              size='sm'
              colorScheme='teal'
              isLoading={isLoading}
              onClick={() => preApprove()}
              isDisabled={isLoading || preApprovedDataMissing || !initialUnderwritingData}
            >
              Pre-Approve
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
